import { useMemo, useState } from "react";
import { parseMiembros, type ParsedAlumno } from "../../lib/import";
import { importarMiembros, type ImportResumen } from "../../lib/data";
import { formatRut, formatFecha } from "../../lib/format";
import { StatCard } from "../../components/ui";

const EJEMPLO = `Martina Mejías Giraldo\t25.816.171-8\t28/6/2017\tU10\tLuz Giraldo\t973215126`;

export function CargaMasiva() {
  const [texto, setTexto] = useState("");
  const [rows, setRows] = useState<ParsedAlumno[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [resumen, setResumen] = useState<ImportResumen | null>(null);
  const [error, setError] = useState("");

  const stats = useMemo(() => {
    if (!rows) return null;
    return {
      total: rows.length,
      conAviso: rows.filter((r) => r.warnings.some((w) => !w.startsWith("Falta correo"))).length,
      rutInvalido: rows.filter((r) => !r.rutValido).length,
    };
  }, [rows]);

  function analizar() {
    setError(""); setResumen(null);
    const { rows } = parseMiembros(texto);
    if (rows.length === 0) { setError("No se detectaron filas. Pega los datos separados por TAB (desde el Excel)."); return; }
    setRows(rows);
  }

  async function confirmar() {
    if (!rows) return;
    setBusy(true); setError("");
    try {
      const r = await importarMiembros(rows);
      setResumen(r);
      setRows(null);
      setTexto("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-800">Carga masiva de miembros</h1>
        <p className="text-sm text-slate-500">
          Pega filas desde el Excel (separadas por TAB): <b>Nombre · RUT alumno · Fecha nac · Categoría · Apoderado · Teléfono</b>.
          Con o sin la columna de fecha/hora inicial.
        </p>
      </div>

      {resumen && (
        <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800">
          ✓ Importación completa: <b>{resumen.alumnosNuevos}</b> alumnos, <b>{resumen.apoderadosNuevos}</b> apoderados nuevos,
          <b> {resumen.cursosNuevos}</b> categorías/cursos. Omitidos (ya existían): {resumen.omitidos}.
        </div>
      )}

      {!rows && (
        <div className="card space-y-3 p-4">
          <textarea
            className="input h-56 font-mono text-xs"
            placeholder={`Pega aquí las filas…\n\nEj:\n${EJEMPLO}`}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <button className="btn-primary" onClick={analizar} disabled={!texto.trim()}>Analizar</button>
            <span className="text-xs text-slate-400">Revisa la previsualización antes de confirmar.</span>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
      )}

      {rows && stats && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Filas detectadas" value={String(stats.total)} tone="brand" />
            <StatCard label="Con advertencias" value={String(stats.conAviso)} tone="amber" />
            <StatCard label="RUT por verificar" value={String(stats.rutInvalido)} tone="rose" />
          </div>

          <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            ⚠ Ningún apoderado trae correo en esta lista, así que <b>no podrán entrar al portal</b> hasta cargarles uno
            (Apoderados → Editar). El resto de los datos se importa igual.
          </div>

          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-100">
                <tr>
                  <th className="th">Alumno</th><th className="th">RUT</th><th className="th">Nac.</th>
                  <th className="th">Categoría</th><th className="th">Apoderado</th><th className="th">Teléfono</th><th className="th">Avisos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((r, i) => (
                  <tr key={i} className={r.rutValido ? "" : "bg-rose-50/40"}>
                    <td className="td font-medium">{r.alumnoNombre}</td>
                    <td className="td">{r.rut ? formatRut(r.rut) : "—"}{!r.rutValido && r.rut && " ⚠"}</td>
                    <td className="td">{formatFecha(r.fechaNacimiento)}</td>
                    <td className="td">{r.categoria || "—"}</td>
                    <td className="td">{r.apoderadoNombre || "—"}</td>
                    <td className="td">{r.apoderadoTelefono || "—"}</td>
                    <td className="td">
                      {r.warnings.filter((w) => !w.startsWith("Falta correo")).length > 0 ? (
                        <span className="badge bg-amber-100 text-amber-700" title={r.warnings.join(" · ")}>
                          {r.warnings.filter((w) => !w.startsWith("Falta correo")).length} aviso(s)
                        </span>
                      ) : (
                        <span className="text-xs text-emerald-600">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={() => setRows(null)}>Volver a editar</button>
            <button className="btn-primary" onClick={confirmar} disabled={busy}>
              {busy ? "Importando…" : `Confirmar e importar ${rows.length}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
